/**
 * @module game/Bomb
 * Bomb entity — slashing a bomb triggers immediate game over in Classic mode.
 *
 * Bombs share the same parabolic physics as fruits but have a dedicated
 * collision response. They do not appear in Zen mode.
 */

import type { Bomb, Vec2 } from '../types.js';
import { CONFIG } from '../game-config.js';
import { stepBody, isOffscreen } from './physics.js';

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let _nextBombId = 0;

// ---------------------------------------------------------------------------
// Minimal PixiJS-compatible interfaces
// ---------------------------------------------------------------------------

interface SpriteRef {
  x: number;
  y: number;
  visible: boolean;
  destroy?(): void;
}

interface ContainerRef {
  addChild(child: SpriteRef): void;
  removeChild(child: SpriteRef): void;
}

/** Bomb with internal render state. */
interface BombInternal extends Bomb {
  _sprite: SpriteRef | null;
}

// ---------------------------------------------------------------------------
// Public functional API
// ---------------------------------------------------------------------------

/**
 * Creates a new bomb entity and adds its sprite to the given container.
 *
 * @param position  - Initial canvas position (px).
 * @param velocity  - Initial velocity vector (px/s).
 * @param container - PixiJS Container to add the sprite to.
 */
export function createBomb(
  position: Vec2,
  velocity: Vec2,
  container: ContainerRef,
): Bomb {
  const sprite: SpriteRef = {
    x: position.x,
    y: position.y,
    visible: true,
  };

  container.addChild(sprite);

  const bomb: BombInternal = {
    id: _nextBombId++,
    x: position.x,
    y: position.y,
    vx: velocity.x,
    vy: velocity.y,
    hitRadius: CONFIG.bomb.hitRadius,
    sprite,
    _sprite: sprite,
  };

  return bomb;
}

/**
 * Update bomb physics for one render frame.
 *
 * @param bomb        - The bomb entity to update.
 * @param dt          - Frame delta in seconds, scaled by current timeScale.
 * @param _canvasWidth - Canvas width (px) — unused but part of contract signature.
 * @param canvasHeight - Canvas height (px) — used to check offscreen.
 * @returns true if the bomb has left the screen (no penalty — just remove).
 */
export function updateBomb(
  bomb: Bomb,
  dt: number,
  _canvasWidth: number,
  canvasHeight: number,
): boolean {
  const b = bomb as BombInternal;

  // Bombs use the same gravity as fruits.
  stepBody(b, dt, CONFIG.fruit.gravity);

  if (b._sprite !== null) {
    b._sprite.x = b.x;
    b._sprite.y = b.y;
  }

  return isOffscreen(b, canvasHeight);
}

/**
 * Trigger bomb explosion: plays explosion animation + SFX.
 * Does NOT call StateMachine.transition — that is the caller's responsibility.
 *
 * Currently marks the sprite invisible; real implementation will play an
 * animated sprite sheet and integrate with the audio module.
 *
 * @param bomb      - The bomb entity to explode.
 * @param container - PixiJS Container (used for future explosion sprite add).
 */
export function explodeBomb(bomb: Bomb, container: ContainerRef): void {
  const b = bomb as BombInternal;

  // Hide sprite immediately — the explosion effect occupies its position.
  if (b._sprite !== null) {
    b._sprite.visible = false;
  }

  // Placeholder: real implementation calls playBomb() + emits explosion particles.
  // The container reference is accepted for when the explosion sprite is added here.
  void container; // suppress unused warning until explosion sprite is wired up
}

/**
 * Remove bomb sprite from scene and free resources.
 *
 * @param bomb      - The bomb entity to destroy.
 * @param container - PixiJS Container to remove sprites from.
 */
export function destroyBomb(bomb: Bomb, container: ContainerRef): void {
  const b = bomb as BombInternal;

  if (b._sprite !== null) {
    container.removeChild(b._sprite);
    b._sprite.destroy?.();
    b._sprite = null;
  }
}
